import {Capacitor} from '@capacitor/core';
import {loadClientConfig} from './core/config.js';
import {createDiagnostics,createStorageDiagnosticBuffer} from './core/diagnostics.js';
import {installGlobalErrorHandlers} from './core/errors.js';
import {resolveFeatureFlags} from './core/feature-flags.js';
import {createUpdateClient} from './core/update-client.js';
import {createAnalytics} from './platform/analytics.js';
import {createAuthAdapter} from './platform/auth.js';
import {createRuntimeConfigAdapter} from './platform/runtime-config.js';
import {createSupabaseClient} from './platform/supabase-client.js';
import {createSessionRuntime} from './account/session-runtime.js';
import {createAuthActions} from './account/auth-actions.js';
import {createAccountDeletionService} from './account/account-deletion.js';
import {accountMarkup as accountViewMarkup} from './account/view.js';
import {createSupabaseAccountDeletionRemote} from './account/account-deletion-remote.js';
import {createIndexedDbRepository} from './offline/indexeddb.js';
import {createPrivateMediaStorage} from './platform/private-media-storage.js';
import {createFeedbackService} from './feedback/service.js';
import {createDiagnosticBundleProvider} from './feedback/diagnostic-bundle.js';
import {createFeedbackAttachmentProvider} from './feedback/attachment.js';
import {createSupabaseFeedbackSubmissionRemote} from './feedback/submission-remote.js';
import {createFeedbackPageController} from './feedback/page-controller.js';
import {createFeedbackPageBindings} from './feedback/page-bindings.js';
import {SMOKEHOUSE_ANALYTICS_EVENTS} from './smokehouse/analytics.js';
import {createSmokehousePageController} from './smokehouse/page-controller.js';
import {createSmokehousePageBindings} from './smokehouse/page-bindings.js';
import {createDymistrzModule} from './dymistrz/module.js';
import {createSupabaseSmokehouseRemoteAdapters} from './smokehouse/supabase-sync.js';
import {createSmokerDefaultRemote} from './smokehouse/smoker-default-remote.js';
import {createSmokerPhotoStorage} from './smokehouse/smoker-photo-storage.js';
import {createSupabaseWoodCatalogueRemote} from './smokehouse/wood-catalogue-remote.js';
import {JournalAnalyticsEvent} from './journal/analytics.js';
import {createJournalRuntime} from './journal/runtime.js';
import {createSupabaseJournalRemoteAdapters} from './journal/supabase-sync.js';
import {createJournalMediaStorage} from './journal/journal-media-storage.js';
import {createJournalMediaRemote} from './journal/journal-media-remote.js';
import {createSupabaseProductCatalogueRemote} from './journal/product-catalogue-remote.js';
import {createJournalPageController} from './journal/page-controller.js';
import {createJournalPageBindings} from './journal/page-bindings.js';
import {button,dangerZone,escapeHtml,message,panel,statusPill,textField} from './ui/components.js';
import {navigationMarkup,resolveRoute} from './ui/navigation.js';
import {authRequiredState,emptyState,errorState,loadingState,offlineState,unavailableState} from './ui/states.js';
import {LIVE_ANALYTICS_EVENTS} from './live/analytics.js';
import {createRecipePlanCache} from './recipes/cache.js';
import {createRecipePageController} from './recipes/page-controller.js';
import {createRecipePageBindings} from './recipes/page-bindings.js';
import {createRecipeUseDraft} from './recipes/use-recipe.js';
import {persistRecipeUseDraft} from './recipes/use-recipe-persistence.js';
import {createRecipeSaveStore} from './recipes/recipe-save-store.js';
import {createRecipeSaveSync} from './recipes/recipe-save-sync.js';
import {createSupabaseRecipeSaveRemote} from './recipes/recipe-save-remote.js';
import {privateRecipeProductIdFromCustomCode} from './recipes/private-product-code.js';
import {createPrivateRecipeService} from './recipes/private-recipe-service.js';
import {RECIPE_ANALYTICS_EVENTS} from './recipes/analytics.js';
import {createPlannerPageController} from './planner/page-controller.js';
import {createPlannerPageBindings} from './planner/page-bindings.js';
import {createLocalNotificationAdapter} from './live/local-notification-adapter.js';
import {isDymistrzPlannerNotification} from './planner/reminder-plan.js';
import {createPlannerReminderPreferenceStore} from './planner/reminder-preferences.js';
import {createPlannerReminderScheduler} from './planner/reminder-scheduler.js';
import {createPlannerAnalytics,PLANNER_ANALYTICS_EVENTS} from './planner/analytics.js';
import {createSupabaseSafetyKnowledgeRemote} from './dymek/safety-knowledge-remote.js';
import {createDymekContextResolver} from './dymek/context-resolver.js';
import {createDymekOrchestrator} from './dymek/orchestrator.js';
import {createDymekPageController} from './dymek/page-controller.js';
import {createDymekPageBindings} from './dymek/page-bindings.js';
import {createDymekServerProvider} from './dymek/provider-remote.js';
import {createDymekAnalytics,DYMEK_ANALYTICS_EVENTS} from './dymek/analytics.js';

const dymistrzDomain=createDymistrzModule();
const config=loadClientConfig();
const appPlatform=String(Capacitor.getPlatform?.()||'web');
let diagnosticStorage=null;
try{diagnosticStorage=globalThis.localStorage;}catch{}
const diagnosticBuffer=createStorageDiagnosticBuffer({storage:diagnosticStorage});
const diagnostics=createDiagnostics({
  appVersion:config.appVersion,
  platform:appPlatform,
  runtime:appPlatform==='web'?'web':'capacitor',
  buffer:diagnosticBuffer
});
installGlobalErrorHandlers(globalThis,diagnostics);

let flags=resolveFeatureFlags();
let backendState=config.supabaseUrl&&config.supabasePublishableKey?'connecting':'not_configured';
let currentSession=null;
let updateState=null;
let sessionRuntime=null;
let authActions=null;
let accountDeletion=null;
let authMessage='';
let authMessageKind='neutral';
let backendBootstrapPromise=null;
let supabaseClient=null;
let smokehouseRepository=null;
let feedbackService=null;
let feedbackAttachmentProvider=null;
let feedbackPage=null;
let feedbackUserId=null;
let feedbackLoadPromise=null;
let feedbackLoadFailed=false;
let feedbackSourceScreen='unknown';
let smokehouseRuntime=null;
let smokehousePage=null;
let smokehouseUserId=null;
let smokehouseLoadPromise=null;
let smokehouseLoadFailed=false;
let smokehouseSyncPromise=null;
let journalRuntime=null;
let journalPage=null;
let journalUserId=null;
let journalLoadPromise=null;
let journalLoadFailed=false;
let journalSyncPromise=null;
let journalBindings=null;
let recipeCache=null;
let recipesPage=null;
let recipesUserId=null;
let recipesLoadPromise=null;
let recipesLoadFailed=false;
let recipeSaveStore=null;
let recipeSaveSync=null;
let privateRecipeService=null;
let plannerPage=null;
let plannerUserId=null;
let plannerLoadPromise=null;
let plannerLoadFailed=false;
let plannerReminderInteractionHandle=null;
let dymekPage=null;
let dymekUserId=null;
let dymekLoadPromise=null;
let dymekLoadFailed=false;

const analytics=createAnalytics({
  allowedEvents:[
    'app_open','screen_view','error',
    ...SMOKEHOUSE_ANALYTICS_EVENTS,
    ...Object.values(JournalAnalyticsEvent),
    ...LIVE_ANALYTICS_EVENTS,
    ...RECIPE_ANALYTICS_EVENTS,
    ...PLANNER_ANALYTICS_EVENTS,
    ...DYMEK_ANALYTICS_EVENTS
  ],
  consent:()=>false,
  dimensions:{
    product:'dymistrz',
    app_version:config.appVersion,
    environment:config.environment,
    platform:appPlatform
  },
  sink:()=>{}
});

const VIEW_COPY=Object.freeze({
  home:Object.freeze({title:'Dymistrz',subtitle:'Cyfrowy warsztat wędzenia: planuj proces, prowadź go krok po kroku i zapamiętuj to, co warto powtórzyć.'}),
  smokings:Object.freeze({title:'Wędzenia',subtitle:'Twoje planowane, aktywne i zakończone procesy wędzenia.'}),
  recipes:Object.freeze({title:'Receptury',subtitle:'Procesy, które można uruchomić, dostosować i później powtarzać.'}),
  planner:Object.freeze({title:'Smart Planner',subtitle:'Powiedz, kiedy jedzenie ma być gotowe. Dymistrz policzy wstecz przygotowanie i proces, a Ty zatwierdzisz lub poprawisz plan.'}),
  dymek:Object.freeze({title:'Dymek',subtitle:'Kontekstowy pomocnik Dymistrza: najpierw zapisane fakty i zweryfikowana wiedza, a sugestie zawsze są oznaczone.'}),
  smokehouse:Object.freeze({title:'Moja wędzarnia',subtitle:'Sprzęt, termometry, drewno i ustawienia Twojego warsztatu.'}),
  profile:Object.freeze({title:'Profil',subtitle:'Logowanie, sesje, prywatność i cykl życia konta.'}),
  feedback:Object.freeze({title:'Feedback',subtitle:'Zgłoś problem lub podziel się pomysłem bez wychodzenia z Dymistrza.'})
});

function setAuthMessage(text='',kind='neutral'){
  authMessage=String(text||'');
  authMessageKind=String(kind||'neutral');
}

function activeHash(){return globalThis.location?.hash||'#/home';}
function authenticated(){return Boolean(currentSession?.user);}

function resetFeedbackRuntime(){
  feedbackService=null;
  feedbackAttachmentProvider=null;
  feedbackPage=null;
  feedbackUserId=null;
  feedbackLoadPromise=null;
  feedbackLoadFailed=false;
  feedbackSourceScreen='unknown';
}

function resetSmokehouseRuntime(){
  smokehouseRuntime=null;
  smokehousePage=null;
  smokehouseUserId=null;
  smokehouseLoadPromise=null;
  smokehouseLoadFailed=false;
  smokehouseSyncPromise=null;
}





function resetDymekRuntime(){
  dymekPage=null;
  dymekUserId=null;
  dymekLoadPromise=null;
  dymekLoadFailed=false;
}

function resetPlannerRuntime(){
  resetDymekRuntime();
  try{void plannerReminderInteractionHandle?.remove?.();}catch{}
  plannerReminderInteractionHandle=null;
  plannerPage=null;
  plannerUserId=null;
  plannerLoadPromise=null;
  plannerLoadFailed=false;
}

function resetJournalRuntime(){
  journalBindings?.dispose?.();
  journalBindings=null;
  resetPlannerRuntime();
  try{void journalRuntime?.dispose?.();}catch{}
  journalRuntime=null;
  journalPage=null;
  journalUserId=null;
  journalLoadPromise=null;
  journalLoadFailed=false;
  journalSyncPromise=null;
}

function resetRecipesRuntime(){
  recipeCache=null;
  recipesPage=null;
  recipesUserId=null;
  recipesLoadPromise=null;
  recipesLoadFailed=false;
  recipeSaveStore=null;
  recipeSaveSync=null;
  privateRecipeService=null;
}

async function ensureFeedbackRuntime(){
  const userId=String(currentSession?.user?.id||'').trim();
  if(!userId||!supabaseClient?.from)return null;
  if(feedbackService&&feedbackUserId===userId)return feedbackService;
  if(feedbackLoadPromise)return feedbackLoadPromise;

  feedbackLoadPromise=(async()=>{
    try{
      if(!smokehouseRepository)smokehouseRepository=createIndexedDbRepository();
      const remote=createSupabaseFeedbackSubmissionRemote({client:supabaseClient});
      const diagnosticBundleProvider=createDiagnosticBundleProvider({
        diagnostics,
        context:()=>({
          appVersion:config.appVersion,
          appBuild:config.appVersion,
          platform:appPlatform,
          runtime:appPlatform==='web'?'web':'capacitor',
          screen:feedbackSourceScreen,
          module:'feedback',
          networkState:globalThis.navigator?.onLine===false?'offline':'online'
        })
      });
      const storage=supabaseClient?.storage?.from
        ?createPrivateMediaStorage({client:supabaseClient})
        :null;
      feedbackAttachmentProvider=storage
        ?createFeedbackAttachmentProvider({storage})
        :null;
      feedbackService=createFeedbackService({
        userId,
        repository:smokehouseRepository,
        remote,
        diagnostics,
        diagnosticBundleProvider,
        attachmentProvider:feedbackAttachmentProvider,
        isOnline:()=>globalThis.navigator?.onLine!==false
      });
      feedbackUserId=userId;
      feedbackLoadFailed=false;
      return feedbackService;
    }catch(error){
      feedbackLoadFailed=true;
      diagnostics.capture('feedback.bootstrap',error);
      return null;
    }finally{
      feedbackLoadPromise=null;
    }
  })();

  return feedbackLoadPromise;
}

async function ensureFeedbackPage(kind='bug'){
  const userId=String(currentSession?.user?.id||'').trim();
  if(!userId)return null;
  const service=await ensureFeedbackRuntime();
  if(!service)return null;
  if(!feedbackPage){
    feedbackPage=createFeedbackPageController({
      service,
      attachmentProvider:feedbackAttachmentProvider,
      userId,
      currentScreen:()=>feedbackSourceScreen,
      isOnline:()=>globalThis.navigator?.onLine!==false
    });
  }
  if(feedbackPage.getState().kind!==kind)feedbackPage.selectKind(kind);
  await feedbackPage.refresh();
  if(resolveRoute(activeHash(),{authenticated:authenticated(),flags}).id==='feedback')render();
  return feedbackPage;
}

async function syncFeedbackPending(){
  if(!authenticated()||globalThis.navigator?.onLine===false)return null;
  try{
    const service=await ensureFeedbackRuntime();
    if(!service)return null;
    const result=await service.retryPending();
    if(feedbackPage)await feedbackPage.refresh();
    if(resolveRoute(activeHash(),{authenticated:authenticated(),flags}).id==='feedback')render();
    return result;
  }catch(error){
    diagnostics.capture('feedback.sync',error);
    return null;
  }
}

function smokehouseProviderDependencies(){
  const syncClient=flags.smokehouseSync===true?supabaseClient:null;
  return Object.freeze({
    remoteAdapters:syncClient?.from?createSupabaseSmokehouseRemoteAdapters({client:syncClient}):null,
    defaultRemote:syncClient?.rpc?createSmokerDefaultRemote({client:syncClient}):null,
    photoStorage:supabaseClient?.storage?.from?createSmokerPhotoStorage({client:supabaseClient}):null,
    woodCatalogueRemote:syncClient?.from?createSupabaseWoodCatalogueRemote({client:syncClient}):null
  });
}

function journalProviderDependencies(){
  const syncClient=flags.journalSync===true?supabaseClient:null;
  return Object.freeze({
    remoteAdapters:syncClient?.from?createSupabaseJournalRemoteAdapters({client:syncClient}):null,
    productCatalogueRemote:syncClient?.from?createSupabaseProductCatalogueRemote({client:syncClient}):null,
    photoStorage:syncClient?.storage?.from?createJournalMediaStorage({client:syncClient}):null,
    photoRemote:syncClient?.from?createJournalMediaRemote({client:syncClient}):null,
    notificationAdapter:createLocalNotificationAdapter()
  });
}

async function ensureSmokehousePage(){
  const userId=String(currentSession?.user?.id||'').trim();
  if(!userId)return null;
  if(smokehousePage&&smokehouseUserId===userId)return smokehousePage;
  if(smokehouseLoadPromise)return smokehouseLoadPromise;

  smokehouseLoadPromise=(async()=>{
    try{
      if(!smokehouseRepository)smokehouseRepository=createIndexedDbRepository();
      smokehouseRuntime=dymistrzDomain.equipment.createRuntime({
        repository:smokehouseRepository,
        userId,
        analytics,
        diagnostics,
        ...smokehouseProviderDependencies(),
        isOnline:()=>globalThis.navigator?.onLine!==false
      });
      smokehousePage=createSmokehousePageController({
        runtime:smokehouseRuntime,
        isOnline:()=>globalThis.navigator?.onLine!==false
      });
      smokehouseUserId=userId;
      smokehouseLoadFailed=false;
      await smokehousePage.refresh();
      return smokehousePage;
    }catch(error){
      smokehouseLoadFailed=true;
      diagnostics.capture('smokehouse.bootstrap',error);
      return null;
    }finally{
      smokehouseLoadPromise=null;
      if(resolveRoute(activeHash(),{authenticated:authenticated(),flags}).id==='smokehouse')render();
    }
  })();
  return smokehouseLoadPromise;
}

async function ensureJournalPage(){
  const userId=String(currentSession?.user?.id||'').trim();
  if(!userId)return null;
  if(journalPage&&journalUserId===userId)return journalPage;
  if(journalLoadPromise)return journalLoadPromise;

  journalLoadPromise=(async()=>{
    try{
      if(!smokehouseRepository)smokehouseRepository=createIndexedDbRepository();
      if(!smokehouseRuntime||smokehouseUserId!==userId){
        smokehouseRuntime=createSmokehouseRuntime({
          repository:smokehouseRepository,
          userId,
          analytics,
          diagnostics,
          ...smokehouseProviderDependencies(),
          isOnline:()=>globalThis.navigator?.onLine!==false
        });
        smokehouseUserId=userId;
        smokehousePage=null;
      }

      journalRuntime=createJournalRuntime({
        repository:smokehouseRepository,
        userId,
        diagnostics,
        analytics,
        observabilityContext:{
          appVersion:config.appVersion,
          environment:config.environment,
          platform:appPlatform
        },
        smokehouse:smokehouseRuntime,
        ...journalProviderDependencies(),
        isOnline:()=>globalThis.navigator?.onLine!==false
      });
      journalPage=createJournalPageController({
        runtime:journalRuntime,
        analytics,
        isOnline:()=>globalThis.navigator?.onLine!==false
      });
      journalUserId=userId;
      journalLoadFailed=false;
      await journalPage.recoverActiveSession();
      return journalPage;
    }catch(error){
      journalLoadFailed=true;
      diagnostics.capture('journal.bootstrap',error);
      return null;
    }finally{
      journalLoadPromise=null;
      if(resolveRoute(activeHash(),{authenticated:authenticated(),flags}).id==='smokings')render();
    }
  })();

  return journalLoadPromise;
}

async function syncRecipeSavesSafe(){
  if(!recipeSaveSync||globalThis.navigator?.onLine===false)return null;
  try{
    return await recipeSaveSync.sync();
  }catch(error){
    diagnostics.capture('recipes.saved-sync',error);
    return null;
  }
}

async function ensurePrivateRecipeService(){
  const userId=String(currentSession?.user?.id||'').trim();
  if(!userId)return null;
  if(privateRecipeService)return privateRecipeService;
  await ensureRecipesPage();
  await ensureJournalPage();
  if(!recipeCache||!journalRuntime)return null;
  privateRecipeService=createPrivateRecipeService({
    journalRuntime,
    recipeCache,
    userId
  });
  return privateRecipeService;
}

async function ensureRecipesPage(){
  const userId=String(currentSession?.user?.id||'').trim();
  if(!userId)return null;
  if(recipesPage&&recipesUserId===userId)return recipesPage;
  if(recipesLoadPromise)return recipesLoadPromise;

  recipesLoadPromise=(async()=>{
    try{
      if(!smokehouseRepository)smokehouseRepository=createIndexedDbRepository();
      recipeCache=createRecipePlanCache({repository:smokehouseRepository});
      recipeSaveStore=createRecipeSaveStore({
        repository:smokehouseRepository,
        userId
      });
      recipeSaveSync=flags.recipes===true&&supabaseClient?.from
        ?createRecipeSaveSync({
          repository:smokehouseRepository,
          store:recipeSaveStore,
          remote:createSupabaseRecipeSaveRemote({client:supabaseClient,userId})
        })
        :null;

      recipesPage=createRecipePageController({
        userId,
        loadEntries:()=>recipeCache.list({userId}),
        loadSaved:async()=>{
          await syncRecipeSavesSafe();
          return recipeSaveStore.list();
        },
        onSaveRecipe:async record=>{
          const result=await recipeSaveStore.toggle(
            record.plan.recipe.id,
            record.plan.recipe.version
          );
          await syncRecipeSavesSafe();
          return result;
        },
        analytics,
        isOnline:()=>globalThis.navigator?.onLine!==false,
        onUseRecipe:async record=>{
          const page=await ensureJournalPage();
          if(!page||!journalRuntime)throw new Error('JOURNAL_UNAVAILABLE');
          const useRequestId=globalThis.crypto?.randomUUID?.()||('recipe-use-'+Date.now());
          const draft=createRecipeUseDraft({
            plan:record.plan,
            userId,
            useRequestId,
            resolveCustomProductId:privateRecipeProductIdFromCustomCode
          });
          const saved=await persistRecipeUseDraft(journalRuntime,draft);
          await page.open(draft.session.id);
          if(globalThis.location)globalThis.location.hash='#/smokings';
          return saved;
        }
      });
      recipesUserId=userId;
      recipesLoadFailed=false;
      await recipesPage.refresh();
      return recipesPage;
    }catch(error){
      recipesLoadFailed=true;
      diagnostics.capture('recipes.bootstrap',error);
      return null;
    }finally{
      recipesLoadPromise=null;
      if(resolveRoute(activeHash(),{authenticated:authenticated(),flags}).id==='recipes')render();
    }
  })();

  return recipesLoadPromise;
}

async function ensurePlannerPage(){
  const userId=String(currentSession?.user?.id||'').trim();
  if(!userId)return null;
  if(plannerPage&&plannerUserId===userId)return plannerPage;
  if(plannerLoadPromise)return plannerLoadPromise;

  plannerLoadPromise=(async()=>{
    try{
      const page=await ensureJournalPage();
      if(!page||!journalRuntime)throw new Error('JOURNAL_UNAVAILABLE');
      const reminderPreferences=createPlannerReminderPreferenceStore({
        repository:smokehouseRepository,
        userId
      });
      const plannerAnalytics=createPlannerAnalytics({analytics});
      const reminderScheduler=createPlannerReminderScheduler({
        adapter:createLocalNotificationAdapter({
          isOwned:isDymistrzPlannerNotification
        }),
        preferenceStore:reminderPreferences,
        listSessions:()=>journalRuntime.sessions.list()
      });
      try{void plannerReminderInteractionHandle?.remove?.();}catch{}
      plannerReminderInteractionHandle=await reminderScheduler.onInteraction(({kind,action})=>{
        plannerAnalytics.notificationInteracted({kind,action});
      });
      plannerPage=createPlannerPageController({
        journalRuntime,
        reminderScheduler,
        plannerAnalytics,
        isOnline:()=>globalThis.navigator?.onLine!==false
      });
      plannerUserId=userId;
      plannerLoadFailed=false;
      await plannerPage.refresh();
      return plannerPage;
    }catch(error){
      plannerLoadFailed=true;
      diagnostics.capture('planner.bootstrap',error);
      return null;
    }finally{
      plannerLoadPromise=null;
      if(resolveRoute(activeHash(),{authenticated:authenticated(),flags}).id==='planner')render();
    }
  })();
  return plannerLoadPromise;
}

async function ensureDymekPage(){
  const userId=String(currentSession?.user?.id||'').trim();
  if(!userId)return null;
  if(dymekPage&&dymekUserId===userId)return dymekPage;
  if(dymekLoadPromise)return dymekLoadPromise;

  dymekLoadPromise=(async()=>{
    try{
      await ensureJournalPage();
      await ensureRecipesPage();
      if(!journalRuntime)throw new Error('JOURNAL_UNAVAILABLE');
      const safetyKnowledge=supabaseClient?.from
        ?createSupabaseSafetyKnowledgeRemote({client:supabaseClient})
        :Object.freeze({retrieve:async()=>Object.freeze([])});
      const resolveContext=createDymekContextResolver({
        journalRuntime,
        recipeCache,
        userId
      });
      const provider=flags.dymekProvider===true&&supabaseClient?.functions?.invoke
        ?createDymekServerProvider({
          client:supabaseClient,
          appVersion:config.appVersion,
          environment:config.environment
        })
        :null;
      const orchestrator=createDymekOrchestrator({
        resolveContext,
        safetyKnowledge,
        provider
      });
      dymekPage=createDymekPageController({
        orchestrator,
        analytics:createDymekAnalytics({analytics})
      });
      dymekUserId=userId;
      dymekLoadFailed=false;
      dymekPage.openContext({surface:'home'});
      return dymekPage;
    }catch(error){
      dymekLoadFailed=true;
      diagnostics.capture('dymek.bootstrap',error);
      return null;
    }finally{
      dymekLoadPromise=null;
      if(resolveRoute(activeHash(),{authenticated:authenticated(),flags}).id==='dymek')render();
    }
  })();
  return dymekLoadPromise;
}

function currentDymekContext(){
  const route=resolveRoute(activeHash(),{authenticated:authenticated(),flags});
  if(route.id==='recipes'){
    const state=recipesPage?.getState?.();
    if(state?.kind==='detail'&&state?.record?.plan?.recipe?.id){
      return Object.freeze({surface:'recipe',recipeId:String(state.record.plan.recipe.id)});
    }
    return Object.freeze({surface:'recipe'});
  }
  if(route.id==='planner'){
    const state=plannerPage?.getState?.();
    const sessionId=state?.session?.id||null;
    const plannerRunId=state?.input?.plannerRunId||state?.applied?.plannerScheduleSnapshot?.plannerRunId||null;
    return Object.freeze({
      surface:'planner',
      sessionId:sessionId?String(sessionId):null,
      plannerRunId:plannerRunId?String(plannerRunId):null
    });
  }
  if(route.id==='smokings'){
    const state=journalPage?.getState?.();
    const session=state?.kind==='editor'?state?.model?.session:null;
    if(session?.id){
      const surface=session.status==='smoking'
        ?'active_session'
        :session.status==='completed'
          ?'completed_session'
          :'session';
      return Object.freeze({
        surface,
        sessionId:String(session.id),
        sourceSessionId:session.sourceSessionId?String(session.sourceSessionId):null,
        plannerRunId:session.plannerScheduleSnapshot?.plannerRunId
          ?String(session.plannerScheduleSnapshot.plannerRunId)
          :null
      });
    }
    return Object.freeze({surface:'session'});
  }
  return Object.freeze({surface:'home'});
}

function dymekEntryMarkup(route){
  if(flags.dymek!==true||!authenticated()||route.id==='dymek')return '';
  if(!['home','recipes','planner','smokings'].includes(route.id))return '';
  return '<div class="view-dymek-entry">'+button({
    id:'open-dymek-context',
    label:'Zapytaj Dymka',
    variant:'secondary'
  })+'</div>';
}

async function recoverActiveJournalOnResume(){
  if(!authenticated())return false;
  try{
    const page=await ensureJournalPage();
    if(!page?.recoverActiveSession)return false;
    await page.recoverActiveSession();

    const route=resolveRoute(activeHash(),{authenticated:true,flags});
    if(route.id==='smokings')render();
    return page.getState?.()?.kind==='editor';
  }catch(error){
    diagnostics.capture('journal.live-recovery.resume',error);
    return false;
  }
}

async function recoverActiveJournalOnBootstrap(){
  if(!authenticated())return false;
  try{
    const page=await ensureJournalPage();
    const state=page?.getState?.();
    const active=state?.kind==='editor'&&state?.model?.session?.status==='smoking';
    const recoveryBlocked=state?.recoveryStatus==='multiple_active_sessions';
    if(!active&&!recoveryBlocked)return false;

    const route=resolveRoute(activeHash(),{authenticated:true,flags});
    if(route.id!=='smokings'&&globalThis.location){
      globalThis.location.hash='#/smokings';
      return true;
    }

    render();
    return true;
  }catch(error){
    diagnostics.capture('journal.live-recovery.bootstrap',error);
    return false;
  }
}

function accountMarkup(){return accountViewMarkup({backendState,currentSession,config});}

function homeMarkup(){
  const signedIn=authenticated();
  const primary=signedIn
    ?'<a class="button button-primary home-primary-action" href="#/smokings">Otwórz moje wędzenia</a>'
    :'<a class="button button-primary home-primary-action" href="#/profile">Zacznij z Dymistrzem</a>';
  const secondary=signedIn
    ?'<a class="button button-ghost" href="#/smokehouse">Moja wędzarnia</a>'
    :'<a class="button button-ghost" href="#/profile">Mam już konto</a>';
  const recipeCard=flags.recipes===true
    ?'<a class="home-action-card" href="#/recipes"><span class="home-action-icon" aria-hidden="true">03</span><strong>Znajdź recepturę</strong><span>Wybierz sprawdzony proces i dopasuj go do swojego sprzętu.</span></a>'
    :'';
  const plannerCard=flags.planner===true
    ?'<a class="home-action-card" href="#/planner"><span class="home-action-icon" aria-hidden="true">04</span><strong>Zaplanuj na godzinę</strong><span>Powiedz, kiedy ma być gotowe — Dymistrz policzy, kiedy zacząć.</span></a>'
    :'';

  return `<section class="home-dashboard" aria-labelledby="home-hero-title">
    <div class="home-hero">
      <div class="home-hero-copy">
        <span class="eyebrow">Cyfrowy warsztat wędzenia</span>
        <h2 id="home-hero-title">Lepsze wędzenie zaczyna się od dobrego planu.</h2>
        <p>Dymistrz prowadzi od przygotowania przez aktywne wędzenie aż do wyniku, który możesz ocenić, zapamiętać i później powtórzyć.</p>
        <div class="actions home-hero-actions">${primary}${secondary}</div>
      </div>
      <div class="home-process" aria-label="Proces z Dymistrzem">
        <div><span>01</span><strong>Zaplanuj</strong><small>sprzęt, produkt, drewno i etapy</small></div>
        <div><span>02</span><strong>Wędź</strong><small>czas, temperatura i szybkie zdarzenia</small></div>
        <div><span>03</span><strong>Ucz się</strong><small>oceń efekt i powtórz najlepszy proces</small></div>
      </div>
    </div>
    <div class="home-section-heading">
      <div><span class="eyebrow">${signedIn?'Co chcesz dziś zrobić?':'Jak działa Dymistrz?'}</span><h3>${signedIn?'Najważniejsze akcje masz od razu pod ręką.':'Zacznij prosto. Szczegóły pojawiają się wtedy, kiedy są potrzebne.'}</h3></div>
    </div>
    <div class="home-action-grid">
      <a class="home-action-card" href="${signedIn?'#/smokings':'#/profile'}"><span class="home-action-icon" aria-hidden="true">01</span><strong>${signedIn?'Prowadź wędzenie':'Załóż konto'}</strong><span>${signedIn?'Zaplanuj nową sesję albo wróć do aktywnego procesu.':'Zaloguj się i zachowuj swoje procesy na wszystkich urządzeniach.'}</span></a>
      <a class="home-action-card" href="${signedIn?'#/smokehouse':'#/profile'}"><span class="home-action-icon" aria-hidden="true">02</span><strong>${signedIn?'Przygotuj wędzarnię':'Dodaj swój sprzęt'}</strong><span>${signedIn?'Ustaw wędzarnię, termometry, drewno i preferencje.':'Dymistrz wykorzysta możliwości Twojej wędzarni przy planowaniu.'}</span></a>
      ${recipeCard}
      ${plannerCard}
    </div>
    <div class="home-promise">
      <span class="home-promise-mark" aria-hidden="true">✓</span>
      <div><strong>Twoje notatki mają zostać użyteczne, nie tylko zapisane.</strong><p>Dymistrz buduje historię procesu tak, żeby kolejna sesja mogła być lepsza od poprzedniej.</p></div>
    </div>
  </section>`;
}
function routeMarkup(route){
  if(route.status==='auth_required')return authRequiredState({text:`${route.label} jest dostępne po zalogowaniu. Przejdź do Profilu, aby się zalogować.`});
  if(route.status==='unavailable')return unavailableState({title:`${route.label} — jeszcze nieaktywne`,text:'Moduł jest przygotowany w nawigacji, ale pozostaje wyłączony flagą funkcjonalną do właściwego etapu roadmapy.'});

  switch(route.id){
    case 'home': return homeMarkup();
    case 'profile': return accountMarkup();
    case 'feedback':
      if(feedbackLoadFailed)return errorState({title:'Nie udało się otworzyć feedbacku',text:'Spróbuj ponownie po chwili.',retryId:'feedback-retry'});
      if(!feedbackPage){
        void ensureFeedbackPage(route.feedbackKind||'bug');
        return loadingState({title:'Ładowanie feedbacku',text:'Przygotowujemy bezpieczny formularz zgłoszenia.'});
      }
      if(feedbackPage.getState().kind!==(route.feedbackKind||'bug'))feedbackPage.selectKind(route.feedbackKind||'bug');
      return feedbackPage.render();
    case 'smokings':
      if(journalLoadFailed)return errorState({title:'Nie udało się otworzyć Dziennika',text:'Dane lokalne pozostają bezpieczne. Spróbuj ponownie.',retryId:'journal-retry'});
      if(!journalPage){
        void ensureJournalPage();
        return loadingState({title:'Ładowanie Dziennika',text:'Odtwarzamy lokalne szkice i zaplanowane wędzenia.'});
      }
      return journalPage.render();
    case 'recipes':
      if(recipesLoadFailed)return errorState({title:'Nie udało się otworzyć Receptur',text:'Lokalny katalog pozostaje bezpieczny. Spróbuj ponownie.',retryId:'recipes-retry'});
      if(!recipesPage){
        void ensureRecipesPage();
        return loadingState({title:'Ładowanie Receptur',text:'Czytamy lokalny katalog i przygotowujemy widok.'});
      }
      return recipesPage.render();
    case 'planner':
      if(plannerLoadFailed)return errorState({title:'Nie udało się otworzyć Smart Plannera',text:'Dane źródłowe nie zostały zmienione. Spróbuj ponownie.',retryId:'planner-retry'});
      if(!plannerPage){
        void ensurePlannerPage();
        return loadingState({title:'Ładowanie Smart Plannera',text:'Czytamy lokalne szkice i możliwości Mojej wędzarni.'});
      }
      return plannerPage.render();
    case 'dymek':
      if(dymekLoadFailed)return errorState({title:'Nie udało się otworzyć Dymka',text:'Dane produktu nie zostały zmienione. Spróbuj ponownie.',retryId:'dymek-retry'});
      if(!dymekPage){
        void ensureDymekPage();
        return loadingState({title:'Ładowanie Dymka',text:'Przygotowujemy minimalny kontekst i zasady dowodowe.'});
      }
      return dymekPage.render();
    case 'smokehouse':
      if(smokehouseLoadFailed)return errorState({title:'Nie udało się otworzyć Mojej wędzarni',text:'Dane lokalne pozostają bezpieczne. Spróbuj ponownie.',retryId:'smokehouse-retry'});
      if(!smokehousePage){
        void ensureSmokehousePage();
        return loadingState({title:'Ładowanie Mojej wędzarni',text:'Odtwarzamy lokalne dane i stan konfiguracji.'});
      }
      return smokehousePage.render();
    default: return emptyState();
  }
}

function feedbackEntryMarkup(route){
  if(!authenticated()||route.id==='feedback')return '';
  return '<aside class="feedback-entry" aria-label="Feedback Dymistrza">'
    +'<span>Pomóż ulepszać Dymistrza</span>'
    +'<div class="feedback-entry-actions">'
      +'<a class="button button-ghost" href="#/feedback/bug" data-feedback-entry="bug">Zgłoś problem</a>'
      +'<a class="button button-ghost" href="#/feedback/idea" data-feedback-entry="idea">Mam pomysł</a>'
    +'</div></aside>';
}
function shellMarkup(route){
  const copy=VIEW_COPY[route.id]||VIEW_COPY.home;
  const online=globalThis.navigator?.onLine!==false;
  const statusLabel=!online
    ?'Offline'
    :updateState?.mandatory
      ?'Wymagana aktualizacja'
      :backendState==='connected'
        ?'Gotowy'
        :backendState==='connecting'
          ?'Łączenie'
          :'Tryb lokalny';
  const statusKind=!online||backendState==='unavailable'?'offline':updateState?.mandatory?'warning':backendState==='connected'?'ready':'local';
  const technicalStatus=`v${config.appVersion} · ${config.environment} · backend ${backendState} · account ${authenticated()?'signed_in':'signed_out'}`;
  const runtime=`<span class="app-readiness app-readiness-${statusKind}" title="${escapeHtml(technicalStatus)}"><span class="app-readiness-dot" aria-hidden="true"></span>${escapeHtml(statusLabel)}</span>`;
  const notice=message({text:authMessage,kind:authMessageKind});
  const connectivity=online?'':offlineState({title:'Pracujesz offline'});
  const viewHeader=route.id==='home'
    ?''
    :`<header class="view-header"><div class="eyebrow">Dymistrz · ${escapeHtml(route.label)}</div><h1>${escapeHtml(copy.title)}</h1><p>${escapeHtml(copy.subtitle)}</p>${dymekEntryMarkup(route)}</header>`;
  return `<div class="app-frame"><header class="topbar"><a class="brand" href="#/home" aria-label="Dymistrz — Home"><img class="brand-mark" src="/assets/brand/dymistrz-icon.svg" alt="" width="36" height="36"><span class="brand-copy"><strong>Dymistrz</strong><span>Adalbird Labs</span></span></a>${runtime}</header>${navigationMarkup(route.hash,{authenticated:authenticated(),flags})}<main class="main-surface" id="main-content">${viewHeader}<div class="view-content">${connectivity}${notice}${routeMarkup(route)}${feedbackEntryMarkup(route)}</div></main></div>`;
}
function render(){
  const root=document.getElementById('app');
  if(!root)return;
  stopJournalLiveTicker();
  const route=resolveRoute(activeHash(),{authenticated:authenticated(),flags});
  root.innerHTML=shellMarkup(route);
  wireInteractiveActions();
  analytics.track('screen_view',{screen:route.id});
}

async function restartBackend(){
  sessionRuntime?.stop();
  sessionRuntime=null;
  backendBootstrapPromise=null;
  supabaseClient=null;
  resetFeedbackRuntime();
  backendState='connecting';
  setAuthMessage('Ponawianie połączenia…','neutral');
  render();
  await bootstrapBackend();
}

async function syncSmokehousePending(){
  if(!smokehouseRuntime?.syncPending||globalThis.navigator?.onLine===false)return null;
  if(smokehouseSyncPromise)return smokehouseSyncPromise;

  smokehouseSyncPromise=(async()=>{
    try{
      const result=await smokehouseRuntime.syncPending();
      if(
        result?.ok
        &&result.processed>0
        &&smokehousePage?.getState?.().kind==='dashboard'
      ){
        await smokehousePage.openDashboard();
        if(resolveRoute(activeHash(),{authenticated:authenticated(),flags}).id==='smokehouse')render();
      }
      return result;
    }catch(error){
      diagnostics.capture('smokehouse.sync',error);
      return null;
    }finally{
      smokehouseSyncPromise=null;
    }
  })();
  return smokehouseSyncPromise;
}

function smokehouseRenderAfter(promise,{sync=true}={}){
  void Promise.resolve(promise)
    .then(()=>{
      render();
      if(sync)void syncSmokehousePending();
    })
    .catch(error=>{
      diagnostics.capture('smokehouse.action',error);
      render();
    });
}

function wireSmokehouseActions(){if(!smokehousePage)return;createSmokehousePageBindings({page:smokehousePage,render,renderAfter:smokehouseRenderAfter,onRetry:()=>{smokehouseLoadFailed=false;return smokehousePage.refresh();}}).bind();}

async function syncJournalPending(){
  if(!journalRuntime?.sync||!journalPage?.syncPending||globalThis.navigator?.onLine===false)return null;
  if(journalSyncPromise)return journalSyncPromise;

  journalSyncPromise=(async()=>{
    try{
      const result=await journalPage.syncPending();
      if(resolveRoute(activeHash(),{authenticated:authenticated(),flags}).id==='smokings')render();
      return result;
    }catch(error){
      diagnostics.capture('journal.sync',error);
      return null;
    }finally{
      journalSyncPromise=null;
    }
  })();
  return journalSyncPromise;
}

function journalRenderAfter(promise,{sync=true}={}){
  void Promise.resolve(promise)
    .then(()=>{
      render();
      if(sync)void syncJournalPending();
    })
    .catch(error=>{
      diagnostics.capture('journal.action',error);
      render();
    });
}

function journalModel(){
  return journalPage?.getState?.()?.model||null;
}





async function createPrivateRecipeFromJournal(){
  const session=journalModel()?.session;
  if(!session?.id)return;
  const entered=globalThis.prompt?.('Nazwa prywatnej receptury',session.title||'Moja receptura');
  if(entered===null)return;
  try{
    const service=await ensurePrivateRecipeService();
    if(!service||!journalRuntime)throw new Error('PRIVATE_RECIPE_SERVICE_UNAVAILABLE');
    const reference=await journalRuntime.referenceData();
    const inventoryKey=new Map((reference?.woodInventory||[])
      .filter(item=>item?.id&&item?.catalogueKey)
      .map(item=>[String(item.id),item.catalogueKey]));
    const result=await service.createFromSession(session.id,{
      title:String(entered||'').trim()||session.title||'Moja receptura',
      resolveWoodKey:wood=>wood.woodKey||inventoryKey.get(String(wood.inventoryItemId||''))||null
    });
    await recipesPage?.refresh?.();
    recipesPage?.setScope?.('mine');
    recipesPage?.open?.(result.plan.recipe.id,{notice:'Utworzono prywatną recepturę z tego procesu.',noticeKind:'success'});
    setAuthMessage('Prywatna receptura została utworzona.','success');
    if(globalThis.location)globalThis.location.hash='#/recipes';
    else render();
  }catch(error){
    diagnostics.capture('recipes.private-from-session',error);
    const code=String(error?.message||'');
    setAuthMessage(
      code==='PRIVATE_RECIPE_WOOD_CODE_REQUIRED'
        ?'Nie można jeszcze zapisać tej sesji jako receptury: użyte własne drewno nie ma kodu katalogowego.'
        :'Nie udało się utworzyć prywatnej receptury z tej sesji.',
      'error'
    );
    render();
  }
}

function wireJournalActions(){
  if(!journalPage)return;
  if(!journalBindings){
    journalBindings=createJournalPageBindings({
      page:journalPage,
      diagnostics,
      getModel:journalModel,
      renderAfter:journalRenderAfter,
      render,
      onRetry:()=>{
        journalLoadFailed=false;
        return journalPage.refresh();
      },
      onCreatePrivateRecipe:createPrivateRecipeFromJournal
    });
  }
  journalBindings.bind();
}

function wireRecipeActions(){if(!recipesPage)return;createRecipePageBindings({page:recipesPage,render,diagnostics,onRetry:()=>{recipesLoadFailed=false;return recipesPage.refresh();}}).bind();}

function plannerRenderAfter(promise,{sync=false}={}){
  void Promise.resolve(promise)
    .then(()=>{
      render();
      if(sync)void syncJournalPending();
    })
    .catch(error=>{
      diagnostics.capture('planner.action',error);
      render();
    });
}

function wirePlannerActions(){if(!plannerPage)return;createPlannerPageBindings({page:plannerPage,render,renderAfter:plannerRenderAfter,diagnostics,onRetry:()=>{plannerLoadFailed=false;return plannerPage.refresh();}}).bind();}

function dymekRenderAfter(promise){
  void Promise.resolve(promise)
    .then(()=>render())
    .catch(error=>{
      diagnostics.capture('dymek.action',error);
      render();
    });
}

function wireDymekActions(){createDymekPageBindings({page:dymekPage,render,renderAfter:dymekRenderAfter,diagnostics,onOpenContext:async()=>{const page=await ensureDymekPage();if(!page)return;page.openContext(currentDymekContext());globalThis.location.hash='#/dymek';render();},onOpenReference:async(kind,ref)=>{if(kind==='session'||kind==='stage'){const sessionId=kind==='stage'?ref.split('/')[0]:ref;await ensureJournalPage();await journalPage?.open?.(sessionId);globalThis.location.hash='#/smokings';}else if(kind==='recipe'){await ensureRecipesPage();recipesPage?.open?.(ref);globalThis.location.hash='#/recipes';}else if(kind==='planner'){await ensurePlannerPage();await plannerPage?.selectSession?.(ref);globalThis.location.hash='#/planner';}render();}}).bind();}

function wireFeedbackActions(){createFeedbackPageBindings({page:feedbackPage,render,diagnostics,onEntry:()=>{feedbackSourceScreen=resolveRoute(activeHash(),{authenticated:authenticated(),flags}).id;},onRetry:()=>{feedbackLoadFailed=false;feedbackPage=null;const route=resolveRoute(activeHash(),{authenticated:authenticated(),flags});void ensureFeedbackPage(route.feedbackKind||'bug');}}).bind();}

function wireInteractiveActions(){
  document.getElementById('retry-backend')?.addEventListener('click',()=>{void restartBackend();});
  wireFeedbackActions();
  wireSmokehouseActions();
  wireJournalActions();
  wireRecipeActions();
  wirePlannerActions();
  wireDymekActions();
  if(!authActions)return;

  const form=document.getElementById('magic-link-form');
  form?.addEventListener('submit',async event=>{
    event.preventDefault();
    const email=new FormData(form).get('email');
    setAuthMessage('Wysyłanie linku…','neutral');
    render();
    try{
      await authActions.requestMagicLink(email);
      setAuthMessage('Link do logowania został wysłany. Sprawdź skrzynkę e-mail.','success');
    }catch{
      setAuthMessage('Nie udało się wysłać linku. Sprawdź adres i spróbuj ponownie.','error');
    }
    render();
  });

  for(const provider of ['google','apple']){
    document.getElementById(`oauth-${provider}`)?.addEventListener('click',async()=>{
      setAuthMessage(`Przekierowanie do ${provider==='google'?'Google':'Apple'}…`,'neutral');
      render();
      try{
        await authActions.signInWithOAuth(provider);
      }catch(error){
        const code=String(error?.safeOAuthCode||'OAUTH_PROVIDER_FAILED');
        setAuthMessage(
          `Nie udało się rozpocząć logowania przez ${provider==='google'?'Google':'Apple'}. Kod: ${code}`,
          'error'
        );
        render();
      }
    });
  }

  document.getElementById('sign-out-local')?.addEventListener('click',async()=>{
    setAuthMessage('Wylogowywanie…','neutral');
    render();
    try{
      await authActions.signOutLocal();
      currentSession=null;
      resetFeedbackRuntime();
      resetSmokehouseRuntime();
      resetJournalRuntime();
      resetRecipesRuntime();
      setAuthMessage('Wylogowano na tym urządzeniu.','success');
    }catch{
      setAuthMessage('Nie udało się wylogować. Spróbuj ponownie.','error');
    }
    render();
  });

  document.getElementById('sign-out-global')?.addEventListener('click',async()=>{
    try{
      const result=await authActions.signOutAllDevices();
      if(result.status==='cancelled')return;
      if(result.status==='offline')setAuthMessage('Do wylogowania wszystkich urządzeń wymagane jest połączenie z internetem.','error');
      else{
        currentSession=null;
        resetFeedbackRuntime();
        resetSmokehouseRuntime();
        resetJournalRuntime();
        resetRecipesRuntime();
        setAuthMessage('Sesje odnawialne zostały wycofane, a to urządzenie wylogowane.','success');
      }
    }catch{
      setAuthMessage('Nie udało się wylogować wszystkich urządzeń. Spróbuj ponownie.','error');
    }
    render();
  });

  document.getElementById('delete-account')?.addEventListener('click',async()=>{
    if(!accountDeletion)return;
    setAuthMessage('Usuwanie konta i prywatnych danych…','neutral');
    render();
    try{
      const result=await accountDeletion.deleteAccount();
      if(result.status==='cancelled')setAuthMessage('','neutral');
      else if(result.status==='offline')setAuthMessage('Usunięcie konta wymaga połączenia z internetem.','error');
      else if(result.ok){
        sessionRuntime?.stop();
        sessionRuntime=null;
        currentSession=null;
        resetFeedbackRuntime();
        resetSmokehouseRuntime();
        resetJournalRuntime();
        resetRecipesRuntime();
        setAuthMessage('Konto Dymistrz i dane konta zostały usunięte.','success');
      }
    }catch(error){
      const code=/^[A-Z0-9_]{3,120}$/.test(String(error?.code||error?.message||''))?String(error?.code||error.message):'ACCOUNT_DELETE_FAILED';
      setAuthMessage(`Nie udało się potwierdzić usunięcia konta. Konto nie jest oznaczane jako usunięte — spróbuj ponownie. Kod: ${code}`,'error');
    }
    render();
  });
}

async function bootstrapBackend(){
  if(backendBootstrapPromise)return backendBootstrapPromise;
  if(!config.supabaseUrl||!config.supabasePublishableKey){backendState='not_configured';render();return;}

  backendBootstrapPromise=(async()=>{
    try{
      const client=createSupabaseClient(config);
      supabaseClient=client;
      const auth=createAuthAdapter({client,diagnostics});
      authActions=createAuthActions({auth,diagnostics});
      const accountDeletionRemote=createSupabaseAccountDeletionRemote({client});
      accountDeletion=createAccountDeletionService({remote:accountDeletionRemote,auth,diagnostics});
      const runtimeConfig=createRuntimeConfigAdapter({client,diagnostics});

      flags=await runtimeConfig.getFeatureFlags();
      const updateClient=createUpdateClient({currentVersion:config.appVersion,manifestLoader:()=>runtimeConfig.getAppVersion('web'),activeProcess:()=>false});
      updateState=await updateClient.check();

      const oauthCallback=authActions.consumeOAuthCallbackError();
      if(oauthCallback.handled){
        const providerLabel=oauthCallback.provider==='apple'?'Apple'
          :oauthCallback.provider==='google'?'Google'
            :'dostawcę logowania';
        setAuthMessage(
          'Nie udało się zakończyć logowania przez '+providerLabel+'. Kod: '+oauthCallback.code,
          'error'
        );
      }

      const callback=await authActions.consumeMagicLinkCallback();
      if(callback.handled){
        currentSession=callback.session;
        setAuthMessage(callback.status==='verified'?'Logowanie zakończone.':'Link logowania jest nieprawidłowy lub wygasł.',callback.status==='verified'?'success':'error');
      }

      sessionRuntime=createSessionRuntime({auth,diagnostics,onChange:({session})=>{
        const previousUser=String(currentSession?.user?.id||'');
        currentSession=session||null;
        const nextUser=String(currentSession?.user?.id||'');
        if(previousUser!==nextUser){resetFeedbackRuntime();resetSmokehouseRuntime();resetJournalRuntime();resetRecipesRuntime();}
        render();
      }});
      await sessionRuntime.start();
      backendState='connected';
      void syncFeedbackPending();
      const liveRecovered=await recoverActiveJournalOnBootstrap();
      if(!liveRecovered)render();
    }catch(error){
      backendState='unavailable';
      currentSession=null;
      resetFeedbackRuntime();
      supabaseClient=null;
      diagnostics.capture('runtime.bootstrap',error);
      setAuthMessage('Backend jest chwilowo niedostępny.','error');
      render();
    }finally{
      backendBootstrapPromise=null;
    }
  })();
  return backendBootstrapPromise;
}

try{
  render();
  analytics.track('app_open',{flags_enabled:Object.values(flags).filter(Boolean).length});
  void bootstrapBackend();
}catch(error){
  diagnostics.capture('app.render',error);
  throw error;
}

globalThis.addEventListener?.('hashchange',render);
globalThis.addEventListener?.('online',()=>{
  render();
  void syncFeedbackPending();
  void syncSmokehousePending();
  void syncJournalPending();
  void syncRecipeSavesSafe().then(async()=>{
    if(recipesPage&&resolveRoute(activeHash(),{authenticated:authenticated(),flags}).id==='recipes'){
      await recipesPage.refresh();
      render();
    }
  });
});
globalThis.addEventListener?.('offline',render);
globalThis.document?.addEventListener?.('visibilitychange',()=>{
  if(globalThis.document?.visibilityState==='visible'){
    void recoverActiveJournalOnResume();
  }
});

if('serviceWorker'in navigator){navigator.serviceWorker.register('/sw.js').catch(error=>diagnostics.capture('service-worker.register',error));}

export function stopRuntime(){sessionRuntime?.stop();}
